import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getActiveFaqs } from "../../../api/faqs";
import {
  Search, ChevronDown, Image as ImageIcon, Video,
  LifeBuoy, Mail, SearchX, X, Link2, AlertTriangle, RotateCw,
  ChevronsDownUp, ChevronsUpDown,
} from "lucide-react";
import { PageHeader } from "../../../components/ui/PageHeader";
import { showToast } from "../../../utils/showToast";
import { FaqAnswer, highlight } from "./faqContent";
import "./ModernClientFaqs.scss";
import { safeEmbedUrl, safeImageSrc } from "../../../utils/safeUrl";

/** Copia al portapapeles con respaldo: la API moderna solo existe en HTTPS. */
async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Permiso denegado o contexto no seguro: se intenta el camino viejo.
  }
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

const norm = (v) => String(v ?? "").toLowerCase();

// `videoUrl` e `image` son campos libres de /admin/faqs: el filtro de esquema vive en
// utils/safeUrl.js y lo comparten las dos vistas de FAQ (portal y panel).

export default function ModernClientFaqs() {
  const { t } = useTranslation();
  // Tres conjuntos, no uno. `openIds` guarda SOLO lo que el usuario abrió a
  // mano y la búsqueda no lo toca; la auto-apertura se deriva de la consulta;
  // `suppressed` recuerda lo que el usuario cerró estando auto-abierto.
  //
  // Con un único Set que la búsqueda reescribía en cada tecla: escribir y
  // borrar cerraba lo que ya estaba abierto, la respuesta abierta por enlace
  // profundo se cerraba al buscar, y una respuesta cerrada a mano se reabría
  // sola en la siguiente pulsación.
  const [openIds, setOpenIds] = useState(() => new Set());
  const [suppressed, setSuppressed] = useState(() => new Set());
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Un fallo de carga NO puede verse igual que "no hay preguntas": el usuario
  // se iría creyendo que la ayuda está vacía cuando el problema es nuestro.
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState("");
  const [reload, setReload] = useState(0);
  const searchRef = useRef(null);
  // El enlace profundo solo debe aplicarse en la primera carga; si no, un
  // refetch volvería a saltar al ancla y a robarle el scroll al usuario.
  const hashApplied = useRef(false);

  const withOut = (set, ids) => {
    const next = new Set(set);
    ids.forEach((id) => next.delete(id));
    return next;
  };
  const withAll = (set, ids) => {
    const next = new Set(set);
    ids.forEach((id) => next.add(id));
    return next;
  };

  useEffect(() => {
    let alive = true;
    const fetchFaqs = async () => {
      setLoading(true);
      try {
        const data = await getActiveFaqs();
        if (!alive) return;
        // El cliente de API atrapa el error y devuelve `{ error }` en vez de
        // lanzar, así que el catch de abajo nunca se enteraba: había que
        // mirar la forma de la respuesta.
        if (!data || data.error || !data.data) {
          setLoadError(true);
          return;
        }
        setLoadError(false);
        const next = Array.isArray(data.data.faqs) ? data.data.faqs : [];
        setFaqs(next);
        // Un reintento puede traer otro conjunto: los ids que ya no existen no
        // se quedan acumulando en los Sets.
        const currentIds = new Set(next.map((f) => f._id));
        const prune = (set) => new Set([...set].filter((id) => currentIds.has(id)));
        setOpenIds(prune);
        setSuppressed(prune);
      } catch (err) {
        console.error("Error cargando las preguntas frecuentes:", err);
        if (alive) setLoadError(true);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchFaqs();
    return () => {
      alive = false;
    };
  }, [reload]);

  // Enlace profundo (#faq-<id>): soporte puede mandar una respuesta concreta.
  const openFromHash = useCallback(() => {
    const id = (window.location.hash || "").replace(/^#faq-/, "");
    if (!id || !faqs.some((f) => f._id === id)) return;
    // Con una búsqueda activa, la pregunta del enlace puede estar fuera del
    // filtro: el ancla quedaba abierta pero sin renderizar, en silencio.
    setQuery("");
    setSuppressed((prev) => withOut(prev, [id]));
    setOpenIds((prev) => new Set(prev).add(id));
    // Dos frames: el primero pinta el panel expandido, el segundo ya tiene su
    // altura real, así que `block: "center"` centra la respuesta y no la fila.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        document
          .getElementById(`faq-${id}`)
          ?.scrollIntoView({ block: "center", behavior: "smooth" });
      })
    );
  }, [faqs]);

  useEffect(() => {
    // Al llegar con el ancla puesta: una sola vez, y nunca en un refetch, o
    // volvería a saltar al ancla robándole el scroll al usuario.
    if (!hashApplied.current && faqs.length) {
      hashApplied.current = true;
      openFromHash();
    }
    // Si el ancla cambia estando ya en la página (un enlace de una respuesta a
    // otra), el componente no se remonta: hay que escuchar el evento.
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, [faqs, openFromHash]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return faqs;
    return faqs.filter(
      (f) => norm(f.title).includes(q) || norm(f.content).includes(q)
    );
  }, [faqs, query]);

  // Se abren solas las respuestas cuyo TEXTO coincide, para que se vea por qué
  // entraron en el resultado. Si lo que coincide es el título no hace falta:
  // ya se ve resaltado en la propia fila. Es estado DERIVADO de la consulta,
  // no una escritura sobre lo que el usuario abrió a mano.
  const autoOpenIds = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return new Set();
    return new Set(
      faqs
        .filter((f) => !norm(f.title).includes(q) && norm(f.content).includes(q))
        .map((f) => f._id)
    );
  }, [faqs, query]);

  const isOpen = (id) =>
    openIds.has(id) || (autoOpenIds.has(id) && !suppressed.has(id));

  const toggleFaq = (id) => {
    if (isOpen(id)) {
      setOpenIds((prev) => withOut(prev, [id]));
      // Cerrar algo que la búsqueda abrió sola tiene que quedar cerrado
      // mientras dure esa búsqueda.
      if (autoOpenIds.has(id)) setSuppressed((prev) => new Set(prev).add(id));
    } else {
      setOpenIds((prev) => new Set(prev).add(id));
      setSuppressed((prev) => withOut(prev, [id]));
    }
  };

  const onQueryChange = (value) => {
    setQuery(value);
    // Al vaciar la búsqueda se olvidan las supresiones, que solo tenían
    // sentido dentro de esa consulta. Lo abierto a mano se conserva.
    if (!value.trim()) setSuppressed(new Set());
  };

  const clearSearch = () => {
    onQueryChange("");
    searchRef.current?.focus();
  };

  const filteredIds = filtered.map((f) => f._id);
  const allOpen = filtered.length > 0 && filtered.every((f) => isOpen(f._id));
  // Solo se toca lo que está a la vista: lo abierto fuera del filtro se
  // conserva y reaparece abierto al limpiar la búsqueda.
  const toggleAll = () => {
    if (allOpen) {
      setOpenIds((prev) => withOut(prev, filteredIds));
      setSuppressed((prev) => withAll(prev, filteredIds.filter((id) => autoOpenIds.has(id))));
    } else {
      setOpenIds((prev) => withAll(prev, filteredIds));
      setSuppressed((prev) => withOut(prev, filteredIds));
    }
  };

  const copyLink = useCallback(
    async (id) => {
      const { origin, pathname } = window.location;
      const ok = await copyText(`${origin}${pathname}#faq-${id}`);
      showToast(ok ? t("Enlace copiado") : t("No pudimos copiar el enlace"), ok);
    },
    [t]
  );

  return (
    <div className="w-full min-w-0 cfaq">
      <PageHeader
        title={t("Preguntas frecuentes")}
        subtitle={t("Encuentra respuestas a las dudas más comunes sobre el servicio.")}
        actions={
          <div className="cfaq__tools">
            <div className="cfaq__search">
              <Search size={17} className="cfaq__search-icon" aria-hidden="true" />
              <input
                ref={searchRef}
                type="search"
                className="cfaq__search-input"
                placeholder={t("Buscar una pregunta...")}
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                aria-label={t("Buscar en preguntas frecuentes")}
              />
              {query && (
                <button
                  type="button"
                  className="cfaq__search-clear"
                  onClick={clearSearch}
                  aria-label={t("Limpiar búsqueda")}
                >
                  <X size={15} aria-hidden="true" />
                </button>
              )}
            </div>
            {filtered.length > 1 && (
              <button type="button" className="cfaq__toolbtn" onClick={toggleAll}>
                {allOpen ? (
                  <ChevronsDownUp size={15} aria-hidden="true" />
                ) : (
                  <ChevronsUpDown size={15} aria-hidden="true" />
                )}
                {allOpen ? t("Contraer todas") : t("Expandir todas")}
              </button>
            )}
          </div>
        }
      />

      {loading ? (
        <div className="cfaq__list">
          {[1, 2, 3, 4].map((i) => (
            <div key={`sk-${i}`} className="cfaq__item cfaq__item--skeleton">
              <div className="cfaq__q">
                <span className="cfaq-skel cfaq-skel--chevron" />
                <span className="cfaq-skel cfaq-skel--line" />
              </div>
            </div>
          ))}
        </div>
      ) : loadError ? (
        <div className="cfaq__error" role="alert">
          <span className="cfaq__error-icon" aria-hidden="true">
            <AlertTriangle size={18} />
          </span>
          <div className="cfaq__error-text">
            <strong>{t("No pudimos cargar las preguntas frecuentes")}</strong>
            <span>{t("Es un problema al mostrarlas, no de tu cuenta. Intenta nuevamente.")}</span>
          </div>
          <button
            type="button"
            className="cfaq__toolbtn"
            onClick={() => setReload((n) => n + 1)}
          >
            <RotateCw size={15} aria-hidden="true" />
            {t("Reintentar")}
          </button>
        </div>
      ) : filtered.length > 0 ? (
        <>
          <p className="cfaq__count" role="status" aria-live="polite">
            {query.trim()
              ? t("{{count}} de {{total}} preguntas", {
                  count: filtered.length,
                  total: faqs.length,
                })
              : ""}
          </p>
          <div className="cfaq__list">
            {filtered.map((faq) => {
              const open = isOpen(faq._id);
              const panelId = `faq-panel-${faq._id}`;
              const buttonId = `faq-btn-${faq._id}`;
              const imageSrc = safeImageSrc(faq.image);
              const videoSrc = safeEmbedUrl(faq.videoUrl);
              return (
                <div
                  key={faq._id}
                  id={`faq-${faq._id}`}
                  className={`cfaq__item ${open ? "cfaq__item--open" : ""}`}
                >
                  <div className="cfaq__head">
                    <h3 className="cfaq__q-heading">
                    <button
                      type="button"
                      id={buttonId}
                      className="cfaq__q"
                      onClick={() => toggleFaq(faq._id)}
                      aria-expanded={open}
                      aria-controls={panelId}
                    >
                      <ChevronDown
                        size={18}
                        className="cfaq__chevron"
                        aria-hidden="true"
                      />
                      <span className="cfaq__q-title">
                        {highlight(faq.title, query)}
                      </span>
                      {(imageSrc || videoSrc) && (
                        <span className="cfaq__media-tag">
                          {imageSrc ? (
                            <ImageIcon size={13} aria-hidden="true" />
                          ) : (
                            <Video size={13} aria-hidden="true" />
                          )}
                          {imageSrc ? t("Imagen") : t("Video")}
                        </span>
                      )}
                    </button>
                    </h3>
                    {/* Enlace directo a una respuesta: soporte deja de tener
                        que decir "entra a FAQ y busca la de las etiquetas". */}
                    <button
                      type="button"
                      className="cfaq__linkbtn"
                      onClick={() => copyLink(faq._id)}
                      title={t("Copiar enlace a esta pregunta")}
                      aria-label={t("Copiar enlace a esta pregunta: {{title}}", {
                        title: faq.title,
                      })}
                    >
                      <Link2 size={16} aria-hidden="true" />
                    </button>
                  </div>

                  {/* El panel existe siempre y se oculta con `hidden`: con
                      render condicional, el `aria-controls` del botón apuntaba
                      a un id inexistente mientras estaba cerrado. Los medios sí
                      se montan solo al abrir, para no cargar los iframes de
                      todas las respuestas al entrar a la página. */}
                  <div
                    className="cfaq__a"
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    hidden={!open}
                  >
                    <FaqAnswer text={faq.content} query={query} />

                    {open && imageSrc && (
                      <div className="cfaq__media">
                        <img
                          src={imageSrc}
                          alt={t("Ilustración de la respuesta")}
                          className="cfaq__media-img"
                          loading="lazy"
                        />
                      </div>
                    )}

                    {open && videoSrc && (
                      <div className="cfaq__media">
                        <div className="cfaq__video">
                          <iframe
                            src={videoSrc}
                            title={t("Video explicativo")}
                            // Sin `allow-same-origin` el reproductor pierde su
                            // sesión; lo que importa es que no herede el origen
                            // del portal, y siendo un host externo no lo hace.
                            sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
                            allow="encrypted-media; picture-in-picture; fullscreen"
                            allowFullScreen
                            className="cfaq__video-frame"
                            loading="lazy"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="cfaq__empty">
          <SearchX size={28} className="cfaq__empty-icon" aria-hidden="true" />
          <h3 className="cfaq__empty-title">
            {query ? t("Sin resultados") : t("No hay preguntas disponibles")}
          </h3>
          <p className="cfaq__empty-text">
            {query
              ? t("No encontramos preguntas que coincidan con \"{{query}}\".", { query })
              : t("Por el momento no hay preguntas frecuentes. Contacta a soporte si necesitas ayuda.")}
          </p>
          {query && (
            <button type="button" className="cfaq__toolbtn" onClick={clearSearch}>
              <X size={15} aria-hidden="true" />
              {t("Limpiar búsqueda")}
            </button>
          )}
        </div>
      )}

      <aside className="cfaq__help">
        <span className="cfaq__help-icon" aria-hidden="true">
          <LifeBuoy size={18} />
        </span>
        <div className="cfaq__help-text">
          <strong>{t("¿No encontraste lo que buscabas?")}</strong>
          <span>{t("Nuestro equipo de soporte está listo para ayudarte.")}</span>
        </div>
        {/* Sin la utilidad `border` de Tailwind: App.js carga bootstrap.min.css
            entero y su `.border` homónima lleva !important, así que este botón
            se pintaba con el gris de Bootstrap en vez del naranja de marca. */}
        <a href="mailto:contacto@beartrackapp.com" className="cfaq__cta">
          <Mail size={15} aria-hidden="true" />
          {t("Contactar soporte")}
        </a>
      </aside>
    </div>
  );
}
